import Button from "@/components/common/Button";
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface Message {
  id: string;
  content: string;
  sender: "user" | "assistant";
  timestamp: Date;
}

const initialMessages: Message[] = [
  {
    id: "1",
    content:
      "Hi there! I'm your Gnzabe AI Assistant. I can help with cybersecurity questions, explain course content, or guide you through security best practices. How can I assist you today?",
    sender: "assistant",
    timestamp: new Date(),
  },
];

const AiAssistant = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: input,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);

    // Simulate AI response after a delay
    setTimeout(() => {
      // Example responses based on input content
      let responseContent = "";

      const lowercaseInput = input.toLowerCase();

      if (
        lowercaseInput.includes("phishing") ||
        lowercaseInput.includes("email") ||
        lowercaseInput.includes("scam")
      ) {
        responseContent =
          "Phishing is when attackers send fraudulent communications that appear to come from a trusted source. To spot phishing emails, look for spelling errors, suspicious links, urgent requests, and mismatched sender addresses. Never click on links in suspicious emails and verify requests for sensitive information through official channels.";
      } else if (
        lowercaseInput.includes("password") ||
        lowercaseInput.includes("strong password")
      ) {
        responseContent =
          "A strong password should be at least 12 characters long and include a mix of uppercase and lowercase letters, numbers, and special characters. Avoid using personal information, dictionary words, or common patterns. Consider using a password manager to generate and store strong, unique passwords for each of your accounts.";
      } else if (
        lowercaseInput.includes("vpn") ||
        lowercaseInput.includes("public wifi")
      ) {
        responseContent =
          "When using public Wi-Fi, always connect through a VPN (Virtual Private Network) to encrypt your internet traffic. Avoid accessing sensitive information like bank accounts or making online purchases on public networks unless you are using a VPN. Also, disable file sharing and auto-connect features when on public networks.";
      } else if (
        lowercaseInput.includes("mfa") ||
        lowercaseInput.includes("two-factor") ||
        lowercaseInput.includes("2fa")
      ) {
        responseContent =
          "Multi-factor authentication (MFA) adds an extra layer of security by requiring two or more verification methods. This typically combines something you know (password) with something you have (like a phone) or something you are (biometrics). Enable MFA whenever possible, especially for email, financial, and other sensitive accounts.";
      } else {
        responseContent =
          "Thanks for your question. Cybersecurity is a complex field, and I would be happy to help with more specific questions about phishing, password management, network security, or any other security topic. Feel free to ask anything related to your courses or general cybersecurity best practices!";
      }

      const assistantMessage: Message = {
        id: Date.now().toString(),
        content: responseContent,
        sender: "assistant",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating button */}
      <div className="fixed bottom-6 right-6 z-50">
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex h-14 w-14 items-center justify-center rounded-full shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
            isOpen
              ? "bg-gray-800 text-white dark:bg-gray-700"
              : "bg-primary-500 text-white dark:bg-primary-600"
          }`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <ChatBubbleLeftRightIcon className="h-6 w-6" />
          )}
        </motion.button>
      </div>

      {/* Chat window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-6 z-50 flex w-full max-w-md flex-col rounded-lg bg-white shadow-xl dark:bg-gray-800 sm:w-96"
          >
            {/* Header */}
            <div className="flex items-center justify-between rounded-t-lg bg-primary-500 px-4 py-3 text-white dark:bg-primary-600">
              <div className="flex items-center">
                <ChatBubbleLeftRightIcon className="mr-2 h-5 w-5" />
                <h3 className="text-sm font-medium">Gnzabe AI Assistant</h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-full p-1 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div
              className="flex-1 overflow-y-auto p-4"
              style={{ maxHeight: "400px" }}
            >
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.sender === "assistant"
                        ? "justify-start"
                        : "justify-end"
                    }`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        message.sender === "assistant"
                          ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                          : "bg-primary-500 text-white dark:bg-primary-600"
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                      <span className="mt-1 block text-right text-xs opacity-70">
                        {message.timestamp.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%] rounded-lg bg-gray-100 px-4 py-2 dark:bg-gray-700">
                      <div className="flex space-x-1">
                        <div className="h-2 w-2 animate-bounce rounded-full bg-gray-400 dark:bg-gray-500"></div>
                        <div
                          className="h-2 w-2 animate-bounce rounded-full bg-gray-400 dark:bg-gray-500"
                          style={{ animationDelay: "0.2s" }}
                        ></div>
                        <div
                          className="h-2 w-2 animate-bounce rounded-full bg-gray-400 dark:bg-gray-500"
                          style={{ animationDelay: "0.4s" }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Input */}
            <div className="border-t border-gray-200 p-4 dark:border-gray-700">
              <div className="flex items-center">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t("assistant.askQuestion")}
                  className="flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                  rows={1}
                  disabled={isTyping}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!input.trim() || isTyping}
                  className="ml-2"
                  variant="primary"
                  size="sm"
                >
                  {isTyping ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <PaperAirplaneIcon className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t("assistant.disclaimer")}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AiAssistant;
